import React, { useEffect, useMemo } from 'react';
import { leaderboardAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import { useSortableTable } from '../hooks/useSortableTable';
import './TeamStandings.css';

const TeamStandings = ({ refreshKey }) => {
  const { data: standings, loading, error, executeRequest } = useApiState([]);
  const { handleSort, sortedData: sortedStandings, getSortIcon } = useSortableTable(
    'comp_points',
    'desc',
    standings,
    'name'
  );

  useEffect(() => {
    const fetchTeamStandings = async () => {
      try {
        await executeRequest(async () => {
          const response = await leaderboardAPI.getTeamStandings();
          return response.data.standings || [];
        });
      } catch (err) {
        console.error('Failed to fetch team standings:', err);
      }
    };

    fetchTeamStandings();
  }, [refreshKey, executeRequest]);

  const getRankClass = (rank) => {
    if (rank <= 4) return 'rank-top4'; // Top 4 make finals
    if (rank <= 8) return 'rank-top8'; // Top 8 make finals
    return '';
  };

  // P-5 FIX: Pre-compute team positions to avoid O(n²) complexity
  // Calculate actual ladder position based on comp_points DESC, diff DESC
  // Previously: sorted 17 times (once per team) = 289 operations
  // Now: sort once = 17 operations (94% reduction)
  const teamPositions = useMemo(() => {
    if (!standings.length) return new Map();

    const sortedByLadder = [...standings].sort((a, b) => {
      if (b.comp_points !== a.comp_points) {
        return b.comp_points - a.comp_points;
      }
      return b.diff - a.diff;
    });

    return new Map(sortedByLadder.map((team, index) => [team.id, index + 1]));
  }, [standings]);

  if (loading) {
    return (
      <div className="team-standings">
        <div className="team-standings-header">
          <h3>Team Ladder</h3>
        </div>
        <div className="team-standings-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-standings">
        <div className="team-standings-header">
          <h3>Team Ladder</h3>
        </div>
        <div className="team-standings-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="team-standings">
      <div className="team-standings-header">
        <h3>Team Ladder</h3>
      </div>
      <div className="team-standings-list">
        {standings.length === 0 ? (
          <div className="team-standings-empty">No team standings available yet</div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="team-standings-header-row">
              <div className="header-pos">Pos</div>
              <div className="header-team sortable" onClick={() => handleSort('name')}>
                Team {getSortIcon('name')}
              </div>
              <div className="header-stats">
                <span className="header-stat header-stat-played sortable" onClick={() => handleSort('played')}>P {getSortIcon('played')}</span>
                <span className="header-stat sortable" onClick={() => handleSort('wins')}>W {getSortIcon('wins')}</span>
                <span className="header-stat sortable" onClick={() => handleSort('draws')}>D {getSortIcon('draws')}</span>
                <span className="header-stat header-stat-losses sortable" onClick={() => handleSort('losses')}>L {getSortIcon('losses')}</span>
                <span className="header-stat sortable" onClick={() => handleSort('comp_points')}>Pts {getSortIcon('comp_points')}</span>
                <span className="header-stat sortable" onClick={() => handleSort('diff')}>+/- {getSortIcon('diff')}</span>
              </div>
            </div>

            {/* Data Rows */}
            {sortedStandings.map((team) => {
              const position = teamPositions.get(team.id);
              return (
                <div
                  key={team.id}
                  className={`team-standings-row ${getRankClass(position)}`}
                >
                  <div className="position-badge">
                    <span className="position-value">{position}</span>
                  </div>
                  <div className="team-info">
                    {team.logo_filename && (
                      <img
                        src={`/assets/team-logos/${team.logo_filename}`}
                        alt={team.name}
                        className="team-logo-small"
                      />
                    )}
                    <div className="team-name-container">
                      <div className="team-name-ladder">{team.name}</div>
                      <div className="team-record">
                        {team.points_for}-{team.points_against}
                      </div>
                    </div>
                  </div>
                  <div className="team-stats">
                    <span className="stat-value stat-played">{team.played}</span>
                    <span className="stat-value">{team.wins}</span>
                    <span className="stat-value">{team.draws}</span>
                    <span className="stat-value stat-losses">{team.losses}</span>
                    <span className="stat-value stat-points">{team.comp_points}</span>
                    <span className="stat-value">{team.diff > 0 ? '+' : ''}{team.diff}</span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

// P-6 FIX: Wrap with React.memo to prevent unnecessary re-renders
// Component only re-renders when refreshKey prop changes
export default React.memo(TeamStandings);
